import type React from 'react';
import type { ReactEventHandler } from 'react';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import type { LunaClient } from '@vlabs/api-bindings';
import { apiContainer } from '@vlabs/api-bindings';
import { BboxRenderer } from '@vlabs/monokit/shared/ui/canvas';
import type { TOriginSize } from '@vlabs/monokit/shared/ui/canvas';

import st from './ImageChecksProcessingImage.module.sass';
import { useChecksResultContext } from '../checksResultContext';
import { BBOX_STYLES } from './constants';

interface ImageChecksProcessingImageProps {
  file?: File;
}

function ImageChecksProcessingImage({
  file = undefined,
}: ImageChecksProcessingImageProps): React.JSX.Element | null {
  const [originSize, setOriginSize] = useState<TOriginSize | undefined>(undefined);
  const src = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
  const { checkResult, bboxList, setBboxList } = useChecksResultContext();

  useEffect(() => {
    (apiContainer.lunaClient as LunaClient).sdk.estimate(file, {
      detect_face: 1,
    }).then((resp) => {
      setBboxList?.(
        resp.data?.images_estimations[0]?.estimations
          .map((estimation) => estimation.face.detection.rect),
      );
    });
  }, [file, setBboxList]);

  const coloredBboxList = useMemo(() => bboxList?.map((bbox) => ({
    ...bbox,
    style: bbox.style ?? BBOX_STYLES[String(checkResult)],
  })), [bboxList, checkResult]);

  const loadImage: ReactEventHandler<HTMLImageElement> = useCallback((e) => {
    const { currentTarget: image } = e;
    if (!image) return;
    setOriginSize({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
  }, []);

  return (
    <div className={st.Root}>
      <div className={st.Inner}>
        <img
          alt="background"
          className={st.Background}
          src={src}
        />
        <img
          alt="object to check"
          className={st.Image}
          onLoad={loadImage}
          src={src}
          style={{
            backgroundImage: src,
          }}
        />
        <BboxRenderer
          bboxList={coloredBboxList}
          originSize={originSize}
        />
      </div>
    </div>
  );
}

export { ImageChecksProcessingImage };
